const toBoolean = (value) => {
  if (value === true || value === 1) return true
  if (value === false || value === 0 || value == null) return false

  const normalized = String(value).trim().toLowerCase()
  return normalized === 'true' || normalized === '1'
}

const getSubscriberProductId = () =>
  window?.themeSubscriberProductId || null

const getSubscriberProductHandle = () =>
  window?.themeSubscriberProductHandle || null

const getVipEnabled = () =>
  toBoolean(window?.__GLOBAL__?.vip?.enable)

const getVipDiscount = () =>
  Number(window?.__GLOBAL__?.vip?.discount || 0)

const getIsVipCustomer = () =>
  toBoolean(window?.__GLOBAL__?.vip?.isVip)

const getDisableNotifySkus = () =>
  window?.__GLOBAL__?.vip?.disable_notify_skus || []

const isSubscriberProduct = (productId, productHandle) => {
  const subscriberProductId = getSubscriberProductId()
  const subscriberProductHandle = getSubscriberProductHandle()

  const matchesId =
    subscriberProductId &&
    String(productId) === String(subscriberProductId)

  const matchesHandle =
    subscriberProductHandle &&
    productHandle &&
    String(productHandle) === String(subscriberProductHandle)

  return Boolean(matchesId || matchesHandle)
}

const cartHasSubscriberProduct = (cart) => {
  if (!cart || !Array.isArray(cart.items)) return false

  return cart.items.some((item) => {
    const productId =
      item?.product_id ??
      item?.productId ??
      item?.product?.id ??
      null

    const productHandle =
      item?.handle ??
      item?.product_handle ??
      item?.product?.handle ??
      null

    return isSubscriberProduct(productId, productHandle)
  })
}

// Mutable cart-derived state.
// Seed from the current cart when available so the first Search render
// uses the correct state immediately.
let vipMembershipInCart = cartHasSubscriberProduct(
  window?.__GLOBAL__?.cart
)

const getAlgoliaVipPricingActive = (cart = null) => {
  if (getIsVipCustomer()) return true

  if (cart && Array.isArray(cart.items)) {
    return cartHasSubscriberProduct(cart)
  }

  return vipMembershipInCart
}

const setSearchPriceVisibility = (element, visible) => {
  if (!element) return

  if (visible) {
    element.style.removeProperty('display')
    element.removeAttribute('aria-hidden')
  } else {
    element.style.setProperty('display', 'none', 'important')
    element.setAttribute('aria-hidden', 'true')
  }
}

const applyAlgoliaVipPriceState = (
  vipPricingActive = getAlgoliaVipPricingActive()
) => {
  document
    .querySelectorAll(
      '#algolia-shopify-instantsearch .ais-hit--price.is-hit--price-vip'
    )
    .forEach((priceElement) => {
      priceElement.classList.toggle('is-member--price-vip', vipPricingActive)
      priceElement.classList.toggle('none-member--price-vip', !vipPricingActive)
      priceElement.dataset.vipCardActive = vipPricingActive ? 'true' : 'false'

      const regularPrice = priceElement.querySelector('.ais-hit--price-regular')
      const salePrice = priceElement.querySelector('.ais-hit--price-sale')
      const memberPrice = priceElement.querySelector('.ais-hit--price-member')
      const memberSalePrice = priceElement.querySelector(
        '.ais-hit--price-member-sale'
      )

      if (salePrice || memberSalePrice) {
        // Sale product:
        // Non-VIP => Sale + compare-at
        // VIP / VIP in cart => Member Sale + compare-at
        setSearchPriceVisibility(salePrice, !vipPricingActive)
        setSearchPriceVisibility(memberSalePrice, vipPricingActive)
      } else {
        // Not on sale:
        // Non-VIP => Regular only
        // VIP / VIP in cart => Member only
        setSearchPriceVisibility(regularPrice, !vipPricingActive)
        setSearchPriceVisibility(memberPrice, vipPricingActive)
      }
    })
}

const isFullAlgoliaCartPayload = (cart) => {
  if (!cart || !Array.isArray(cart.items)) return false

  return (
    typeof cart.item_count === 'number' ||
    typeof cart.total_price === 'number' ||
    typeof cart.original_total_price === 'number' ||
    typeof cart.token === 'string'
  )
}

const getCartFromAlgoliaEvent = (event) => {
  const detail = event?.detail
  if (!detail) return null

  if (detail.cart && isFullAlgoliaCartPayload(detail.cart)) {
    return detail.cart
  }

  if (isFullAlgoliaCartPayload(detail)) {
    return detail
  }

  if (detail.data?.cart && isFullAlgoliaCartPayload(detail.data.cart)) {
    return detail.data.cart
  }

  return null
}

const fetchFreshAlgoliaCart = async () => {
  const root = window?.Shopify?.routes?.root || '/'
  const response = await fetch(`${root}cart.js`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (!response.ok) {
    throw new Error(`Unable to load cart state: ${response.status}`)
  }

  return response.json()
}

let algoliaVipPriceSyncTimer = null
let algoliaVipPriceSyncPromise = null
let algoliaVipPriceSyncQueued = false

const syncAlgoliaVipPriceState = async (event) => {
  // Immediate update for the VIP offer flow.
  if (
    event?.type === 'vipOffer:stateUpdated' &&
    'hasVip' in (event.detail || {})
  ) {
    vipMembershipInCart = toBoolean(event.detail.hasVip)
    applyAlgoliaVipPriceState(
      getIsVipCustomer() || vipMembershipInCart
    )
    // Keep the immediate UI update, then confirm with fresh /cart.js below.
  }

  const eventCart = getCartFromAlgoliaEvent(event)
  if (eventCart) {
    vipMembershipInCart = cartHasSubscriberProduct(eventCart)

    if (window.__GLOBAL__) {
      window.__GLOBAL__.cart = eventCart
    }

    applyAlgoliaVipPriceState(
      getIsVipCustomer() || vipMembershipInCart
    )
  }

  // Logged-in VIP accounts always keep member pricing,
  // but still allow cart state to be refreshed for consistency.
  try {
    const cart = await fetchFreshAlgoliaCart()

    vipMembershipInCart = cartHasSubscriberProduct(cart)

    if (window.__GLOBAL__) {
      window.__GLOBAL__.cart = cart
    }

    applyAlgoliaVipPriceState(
      getIsVipCustomer() || vipMembershipInCart
    )
  } catch (error) {
    console.warn('Unable to sync Algolia VIP pricing state:', error)
  }
}

const runAlgoliaVipPriceSync = async (event) => {
  if (algoliaVipPriceSyncPromise) {
    algoliaVipPriceSyncQueued = true
    return algoliaVipPriceSyncPromise
  }

  algoliaVipPriceSyncPromise = (async () => {
    let nextEvent = event

    do {
      algoliaVipPriceSyncQueued = false
      await syncAlgoliaVipPriceState(nextEvent)
      nextEvent = undefined
    } while (algoliaVipPriceSyncQueued)
  })()

  try {
    await algoliaVipPriceSyncPromise
  } finally {
    algoliaVipPriceSyncPromise = null
  }
}

const scheduleAlgoliaVipPriceSync = (event, delay = 100) => {
  clearTimeout(algoliaVipPriceSyncTimer)

  algoliaVipPriceSyncTimer = setTimeout(() => {
    runAlgoliaVipPriceSync(event)
  }, delay)
}

;[
  'cart:updated',
  'cart:changed',
  'productForm:afterAdd',
  'vipOffer:stateUpdated',
].forEach((eventName) => {
  document.addEventListener(eventName, (event) => {
    scheduleAlgoliaVipPriceSync(event)
  })
})

/*
 * First-entry fix:
 * Search assets can execute before __GLOBAL__.vip / subscriber settings
 * are available. Retry briefly instead of freezing stale values at load.
 */
const waitForAlgoliaVipGlobalsAndSync = () => {
  let attempts = 0
  const maxAttempts = 30

  const check = () => {
    attempts += 1

    const vipGlobalReady =
      window?.__GLOBAL__?.vip &&
      typeof window.__GLOBAL__.vip.enable !== 'undefined'

    const subscriberReady =
      Boolean(getSubscriberProductId()) ||
      Boolean(getSubscriberProductHandle())

    if (vipGlobalReady && subscriberReady) {
      scheduleAlgoliaVipPriceSync(undefined, 0)
      return
    }

    if (attempts < maxAttempts) {
      setTimeout(check, 100)
      return
    }

    // Fallback: still sync cart even if one global was delayed/missing.
    scheduleAlgoliaVipPriceSync(undefined, 0)
  }

  check()
}

/*
 * Some first-entry Search flows do not dispatch the theme's custom
 * cart events. Observe only Shopify cart mutation fetches.
 */
if (!window.__algoliaVipCartFetchObserverInitialized) {
  window.__algoliaVipCartFetchObserverInitialized = true

  const previousFetch = window.fetch.bind(window)

  window.fetch = (...args) => {
    const request = args[0]
    const url =
      typeof request === 'string'
        ? request
        : request?.url || ''

    const isCartMutation =
      /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url)

    const requestPromise = previousFetch(...args)

    if (!isCartMutation) {
      return requestPromise
    }

    return requestPromise
      .then((response) => {
        scheduleAlgoliaVipPriceSync(undefined, 75)
        return response
      })
      .catch((error) => {
        scheduleAlgoliaVipPriceSync(undefined, 150)
        throw error
      })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    waitForAlgoliaVipGlobalsAndSync()
  })
} else {
  waitForAlgoliaVipGlobalsAndSync()
}

window.addEventListener('pageshow', () => {
  waitForAlgoliaVipGlobalsAndSync()
})


// assign vip_tag = settings.vip_tag | strip | downcase


const handleize = (value) => {
  if (!value) return ''
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const formatHitPrice = (hit, priceValue) => {
  const numericPrice = Number(priceValue)
  if (Number.isNaN(numericPrice)) return ''
  const tempHit = { ...hit, price: numericPrice }
  return algoliaShopify.helpers.displayPrice(tempHit, hit._distinct)
}

const calculateMemberPrice = (priceValue) => {
  const vipEnabled = getVipEnabled()
  const vipDiscount = getVipDiscount()

  if (!vipEnabled || vipDiscount <= 0) return null

  const numericPrice = Number(priceValue)
  if (Number.isNaN(numericPrice)) return null

  const discount = (numericPrice * vipDiscount) / 100
  return Math.max(numericPrice - discount, 0)
}

let nonInventoriedProductArr = []

document.addEventListener('algolia.hooks.initialize', function () {
  // Autocomplete
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteOptions',
    function (options) {
      // Modify default options, then return them
      options.panelContainer = '#js-autocomplete-container'
      options.detachedMediaQuery = 'none'
      options.placeholder =
        document.getElementById('autocomplete')?.dataset.searchPlaceholder ||
        'What are you looking for?'
      return options
    }
  )
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteFooterTemplate',
    function (_defaultTemplate, templateOptions, resource) {
      const { html, state } = templateOptions
      const { context, query } = state
      // Modify default options, then return them
      return html`
        <div class="aa-SourceFooter search-results__footer">
          <div class="aa-footer search-results__footer__wrapper">
            <a class="aa-SeeAllBtn view-all__btn" href="/search?q=${query}">
              View all <span>${context.nbProducts}</span> results
            </a>
          </div>
        </div>
      `
    }
  )
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteProductTemplate',
    function (_defaultTemplate, templateOptions, distinct, itemLink) {
      const { html, item, components } = templateOptions
      // Modify default options, then return them
      return html`
        <a
          href="${itemLink}"
          class="aa-ItemLink aa-ProductItem search-results__link"
          aria-label="${item.title}"
        >
          <div class="aa-ItemContent search-results__item">
            <div
              class="aa-ItemPicture aa-ItemPicture--search-results__item-image"
            >
              <img
                src="${item.image}"
                alt="${item.title}"
                class="image"
                loading="lazy"
              />
            </div>
            <div class="aa-ItemContentBody search-results__body">
              <div
                class="aa-ItemContentTitleWrapper search-results__title-wrapper"
              >
                <div class="aa-ItemContentTitle search-results__title">
                  <p>
                    ${components.Highlight({ hit: item, attribute: 'title' })}
                  </p>
                </div>
                <div class="aa-ItemContentType search-results__type">
                  <p>
                    Tea Type:
                    ${components.Highlight({
                      hit: item,
                      attribute: 'product_type',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </a>
      `
    }
  )

  // InstantSearch
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchOptions',
    function (options) {
      options.hitsPerPage = 999
      return {
        ...options,
      }
    }
  )

  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchStatsTemplate',
    function (_defaultTemplate, data, html) {
      return html`
        ${data.hasOneResult &&
        html`
          <p class="mv2 light js--update-result-count" role="status">
            Currently displaying 1 item
          </p>
        `}
        ${data.hasManyResults &&
        html`
          <p class="mv2 light js--update-result-count" role="status">
            Currently displaying ${' '}
            <strong>${algoliaShopify.helpers.formatNumber(data.nbHits)}</strong> items
          </p>
        `}
      `
    }
  )

  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchProductTemplate',
    function (_defaultTemplate, hit, html, components) {
      const productTypeValue =
        hit.product_type ||
        hit.meta?.custom?.product_type?.[0] ||
        hit.meta?.description?.product_type?.[0] ||
        ''
      const productTypeHandle = handleize(productTypeValue)
      const isGiftCardTag = Array.isArray(hit.tags)
        ? hit.tags.some((tag) => handleize(tag).includes('gift-card'))
        : false
      const isGiftCard =
        productTypeHandle.includes('gift-card') || isGiftCardTag

      const bzvId = () => {
        if (hit.meta?.bzv?.review_product) {
          const productId = hit.meta?.bzv?.review_product.split('/')
          return productId[productId.length - 1]
        } else {
          return hit.id
        }
      }

      if (hit.meta?.custom?.product_type?.[0] == 'Blends') {
        nonInventoriedProductArr.push(hit)
        return
      }

      const isNonInventoried = hit.template_suffix == 'non-inventoried'
      const isBundle = hit.tags?.includes('fastbundle')
      const oosProduct = hit.tags?.includes('out-of-stock')
      const isBisProduct = hit.can_order == false
      const isSelectTea = hit.tags?.includes('SELECT TEA')
      const isViewProduct = hit.tags?.includes('VIEW PRODUCT')

      const parseHtml = (html) => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        return doc.body.textContent.trim()
      }

      return html`
        <hh-product-card-variants
          class="ais-hit ais-product custom-product product-card"
          data-algolia-index="${hit.index}"
          data-algolia-position="${hit.productPosition}"
          data-algolia-queryid="${hit.queryID}"
          data-algolia-objectid="${hit.objectID}"
          data-handle="${hit.handle}"
          data-variant-id="${hit.objectID}"
          data-distinct="${hit._distinct}"
          data-product-id="${hit.id}"
        >
          <div class="product-card__info">
            <a
              class="product-card__title--link"
              href="${algoliaShopify.helpers.instantsearchLink(hit)}"
            >
              <p
                class="product-card__title text-utility text-utility--medium c-teal"
              >
                ${hit.title}
              </p>
            </a>

            ${isNonInventoried && hit.meta?.description?.secondary?.length
              ? html`<div
                  class="product-card__description text-utility text-utility--small c-gold"
                >
                  <a href="${algoliaShopify.helpers.instantsearchLink(hit)}"
                  class="product-card__description--secondary-link"  >${hit.meta.description.secondary[0]}</a
                  >
                </div>`
              : null}
            ${!isNonInventoried && hit.meta?.custom?.discount_field_text?.length
              ? html`<div
                  class="product-card__description text-utility text-utility--small"
                  style="color: ${hit.meta?.custom?.discount_field_text_color || __GLOBAL__.discountTextDefaultColor || '#b8070c'} !important;"
                >
                  <a
                    href="${algoliaShopify.helpers.instantsearchLink(hit)}"
                    class="product-card__description--discount"
                    style="color: inherit !important;"
                  >
                    ${hit.meta.custom.discount_field_text[0]}
                  </a>
                </div>`
              : null}
          </div>

          <a
            class="js-anchor product-card__link"
            href="${algoliaShopify.helpers.instantsearchLink(hit)}"
          >
            <div class="product-card__media">
              <img
                class="ais-hit--picture product-media"
                src="${algoliaShopify.helpers.largeImage(hit)}"
                alt="${hit.title} - ${hit.variant_title}"
                loading="lazy"
              />
            </div>
          </a>

          ${isNonInventoried
            ? html`
                <div class="product-card__info-footer non-inventoried-product">
                  ${hit.meta.description?.entry?.length
                    ? html`<div
                        class="product-card__description gold text-utility text-utility--small text-utility--alt c-gold"
                      >
                        <a
                          href="${algoliaShopify.helpers.instantsearchLink(
                            hit
                          )}"
                          >${parseHtml(hit.meta.description.entry[0])}</a
                        >
                      </div>`
                    : null}

                  <div class="product-card__info-footer-content">
                    <div>
                      <div
                        data-bv-show="inline_rating"
                        data-bv-product-id="${bzvId()}"
                        data-bv-redirect-url="${algoliaShopify.helpers.instantsearchLink(
                          hit
                        )}"
                        data-bv-seo="false"
                      ></div>
                    </div>

                    ${hit.meta.description?.tags?.length
                      ? html`<p
                          class="product-card__tags text-utility text-utility--smaller text-utility--medium"
                        >
                          ${hit.meta.description.tags.map((tag) => {
                            return html`<span class="product-card__tag"
                              >${tag}</span
                            >`
                          })}
                        </p> `
                      : null}
                  </div>
                </div>

                <div
                  class="product-card__atc"
                  style="--hh-padding: 0.5rem 1rem"
                >
                  <hh-button-wrapper
                    theme="secondary"
                    class="view-bundle cta__action-btn"
                    style="--hh-padding: 0.5rem 1rem;"
                  >
                    <a href="${algoliaShopify.helpers.instantsearchLink(hit)}">
                      Explore this blend
                    </a>
                  </hh-button-wrapper>
                </div>
              `
            : html`<div class="product-card__info-footer inventoried-product">
                    <div class="product-card__info-footer-content">
                    <div class="product-card__info-footer-content-left">
                      ${!oosProduct
                        ? html`
                            ${(() => {
                              const subscriber = isSubscriberProduct(
                                hit.id,
                                hit.handle
                              )
                              const hasCompare =
                                Number(hit.compare_at_price) >
                                Number(hit.price)
                              const vipEnabledForHit =
                                getVipEnabled() && !isGiftCard
                              const showMember =
                                vipEnabledForHit && !subscriber
                              const regularLabel =
                                isGiftCard ? '' : subscriber ? 'Price' : 'Regular'
                              const regularClass = hasCompare
                                ? 'ais-hit--price-striked text-utility'
                                : 'ais-hit--price-regular'
                              const saleLabel = isGiftCard
                                ? ''
                                : subscriber
                                ? hasCompare
                                  ? 'First month'
                                  : 'Price'
                                : hasCompare
                                ? 'Sale'
                                : 'Regular'
                              const memberLabel = hasCompare
                                ? 'VIP Member'
                                : 'VIP Member'
                              const memberPriceValue =
                                showMember && !hasCompare
                                  ? calculateMemberPrice(hit.price)
                                  : null
                              const memberSalePriceValue =
                                showMember && hasCompare
                                  ? calculateMemberPrice(hit.price)
                                  : null
                              const regularPriceValue = hasCompare
                                ? formatHitPrice(hit, hit.compare_at_price)
                                : formatHitPrice(hit, hit.price)
                              const salePriceValue = formatHitPrice(
                                hit,
                                hit.price
                              )
                              const memberPriceTemplate =
                                showMember && memberPriceValue !== null
                                  ? html`<span class="ais-hit--price-member">
                                      <span class="price-label-text"
                                        >${memberLabel}</span
                                      >
                                      <span>
                                        ${formatHitPrice(
                                          hit,
                                          memberPriceValue
                                        )}
                                      </span>
                                    </span>`
                                  : null
                              const memberSaleTemplate =
                                showMember &&
                                hasCompare &&
                                memberSalePriceValue !== null
                                  ? html`<span class="ais-hit--price-member-sale">
                                      <span class="price-label-text"
                                        >${memberLabel}</span
                                      >
                                      <span>
                                        ${formatHitPrice(
                                          hit,
                                          memberSalePriceValue
                                        )}
                                      </span>
                                    </span>`
                                  : null

                              const vipPricingActive =
                                vipEnabledForHit &&
                                getAlgoliaVipPricingActive()

                              const vipPriceStatusClass =
                                vipEnabledForHit
                                  ? vipPricingActive
                                    ? 'is-member--price-vip'
                                    : 'none-member--price-vip'
                                  : ''

                              return html`<p
                                class="ais-hit--price ${vipEnabledForHit ? 'is-hit--price-vip' : ''} ${vipPriceStatusClass}"
                                data-vip-card-active="${vipPricingActive ? 'true' : 'false'}"
                              >
                                <span class="${regularClass}">
                                  ${regularLabel
                                    ? html`${regularPriceValue} <span class="price-label-text"
                                        >  </span
                                      >`
                                    : ''}
                                </span>
                                ${hasCompare
                                  ? html`<span class="ais-hit--price-sale c-red">
                                      ${saleLabel
                                        ? html`<span class="price-label-text"
                                            >${saleLabel}</span
                                          >`
                                        : ''}
                                      <span>${salePriceValue}</span>
                                    </span>`
                                  : ''}
                                ${subscriber
                                  ? ''
                                  : hasCompare
                                  ? memberSaleTemplate
                                  : memberPriceTemplate}
                              </p>`
                            })()}
                          `
                        : null}
                      <div
                        data-bv-show="inline_rating"
                        data-bv-product-id="${bzvId()}"
                        data-bv-redirect-url="${algoliaShopify.helpers.instantsearchLink(
                          hit
                        )}"
                        data-bv-seo="false"
                      ></div>
                    </div>

                    <div class="product-card__info-footer-content-right">
                      ${hit.meta?.description?.secondary?.length
                        ? html`<p
                            class="product-card__description text-utility text-utility--small c-gold"
                          >
                            <a
                              href="${algoliaShopify.helpers.instantsearchLink(
                                hit
                              )}"
                              >${hit.meta.description.secondary[0]}</a
                            >
                          </p>`
                        : null}

                      <p
                        class="product-card__description text-utility text-utility--small c-gold"
                      >
                        <a
                          href="${algoliaShopify.helpers.instantsearchLink(
                            hit
                          )}"
                          >${hit.product_type}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                <div class="product-card__entry">
                  ${__GLOBAL__.showProductCardATC
                    ? html` <div class="product-card__atc">
                        ${isNonInventoried &&
                        !isBisProduct &&
                        !oosProduct &&
                        html`<hh-button-wrapper
                          theme="secondary"
                          class="view-bundle cta__action-btn"
                        >
                          <a
                            href="${algoliaShopify.helpers.instantsearchLink(
                              hit
                            )}"
                          >
                            View Products
                          </a>
                        </hh-button-wrapper>`}
                        ${!isNonInventoried &&
                        !oosProduct &&
                        !isBisProduct &&
                        !isBundle &&
                        !isSelectTea &&
                        !isViewProduct &&
                        hit.price > 0 &&
                        html` <hh-broadcast
                          on="productAdded"
                          event="modal"
                          class="block"
                        >
                          <hh-product-form show-mini-cart class="block">
                            <form
                              method="post"
                              action="/cart/add"
                              id="card-product-form-${hit.objectID}"
                              accept-charset="UTF-8"
                              class="form shopify-product-form"
                              enctype="multipart/form-data"
                              novalidate
                              data-type="add-to-cart-form"
                            >
                              <input
                                type="hidden"
                                name="form_type"
                                value="product"
                              />
                              <input type="hidden" name="utf8" value="✓" />
                              <input name="quantity" type="hidden" value="1" />
                              <input
                                type="hidden"
                                name="id"
                                value="${hit.objectID}"
                              />
                              <div class="product-form__actions">
                                <div class="product-form__buttons">
                                  <hh-button-wrapper
                                    class="atc__btn"
                                    width="full"
                                    theme="secondary"
                                  >
                                    <button
                                      type="submit"
                                      name="add"
                                      class="js-submit-button cta__action-btn"
                                    >
                                      Add to Bag
                                    </button>
                                  </hh-button-wrapper>
                                </div>
                              </div>
                              <input
                                type="hidden"
                                name="product-id"
                                value="${hit.objectID}"
                              />
                            </form>
                          </hh-product-form>
                        </hh-broadcast>`}
                        ${oosProduct
                          ? html`
                              <hh-button-wrapper
                                theme="tertiary"
                                class="bis-cta__btn cta__action-btn view-bundle"
                              >
                                <a
                                  href="${algoliaShopify.helpers.instantsearchLink(
                                    hit
                                  )}"
                                >
                                  Out of stock
                                </a>
                              </hh-button-wrapper>
                            `
                          : ''}
                            ${isSelectTea
                          ? html`
                              <div class="product-card-addon__actions">
                                <hh-button-wrapper
                                  theme="secondary"
                                  class="select-tea view-bundle"
                                >
                                  <a
                                    href="${algoliaShopify.helpers.instantsearchLink(
                                      hit
                                    )}"
                                  >
                                    Select Teas
                                  </a>
                                </hh-button-wrapper>
                              </div>
                            `
                          : ''}
                            ${isViewProduct
                          ? html`
                              <div class="product-card-addon__actions">
                                <hh-button-wrapper
                                  theme="secondary"
                                  class="select-tea view-bundle"
                                >
                                  <a
                                    href="${algoliaShopify.helpers.instantsearchLink(
                                      hit
                                    )}"
                                  >
                                    View Product
                                  </a>
                                </hh-button-wrapper>
                              </div>
                            `
                          : ''}
                        ${isBundle &&
                        !isBisProduct &&
                        html`<hh-button-wrapper
                          theme="secondary"
                          class="view-bundle cta__action-btn view-bundle"
                        >
                          <a
                            href="${algoliaShopify.helpers.instantsearchLink(
                              hit
                            )}"
                          >
                            View Bundle
                          </a>
                        </hh-button-wrapper>`}
                        ${!isNonInventoried &&
                        !oosProduct &&
                        isBisProduct &&
                        !getDisableNotifySkus().includes(hit.sku) &&
                        html`<hh-broadcast
                            event="modal"
                            data='{"id": "bis-modal__product--${hit.id}"}'
                            class="no-js-hidden"
                            disable-body-scroll
                          >
                            <hh-button-wrapper
                              theme="tertiary"
                              id="bisModalBtn--${hit.id}"
                              class="bis-cta__btn"
                              type="button"
                              aria-haspopup="dialog"
                            >
                              <button class="cta__action-btn">Email When Available</button>
                            </hh-button-wrapper>
                          </hh-broadcast>
                          <hh-modal
                            id="bis-modal__product--${hit.id}"
                            class="bis-modal bis-modal__product"
                          >
                            <form-bis-notification
                              product-id="${hit.id}"
                              product-title="${hit.title}"
                              variant-id="${hit.objectID}"
                            >
                            </form-bis-notification>
                          </hh-modal>`}
                      </div>`
                    : ''}
                </div> `}
        </hh-product-card-variants>
      `
    }
  )

  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchMainTemplate',
    function (_defaultTemplate, data, html) {
      const productTemplate = html`
        <div class="ais-page container">

          <div class="ais-facets-button">Show filters</div>
          <div class="ais-facets ${!__GLOBAL__.showAlgoliaSortBy && !__GLOBAL__.showAlgoliaFilters ? 'hidden' : ''} ">
            <div class="ais-input">
              <div class="ais-search-box-container"></div>
              <div class="ais-input-button">
                <div class="ais-clear-input-icon"></div>
              </div>
            </div>
            <div class="ais-clear-refinements-container"></div>
            <div class="ais-current-refined-values-container"></div>
            <div class="ais-sort ${__GLOBAL__.showAlgoliaSortBy ? '' : 'hidden'}">
              ${data.multipleSortOrders
                ? html`
                    ${algoliaShopify.translations.sortBy}
                    <span class="ais-sort-orders-container"></span>
                  `
                : html`
                    ${algoliaShopify.translations.sortBy}
                    ${algoliaShopify.translations.relevance}
                  `
                }
            </div>
            <div class="${__GLOBAL__.showAlgoliaFilters ? '' : 'hidden'}">
              ${data.facets.map(
                (facet) =>
                  html`
                    <div
                      class="ais-facet-dropdown-wrapper ais-facet-${facet.type} ais-facet-${facet.escapedName} accordion--filter"
                    >
                      <input
                        class="ais-dropdown-checkbox"
                        type="checkbox"
                        id="${facet.escapedName}"
                        name="dropdown"
                      />
                      <label
                        for="${facet.escapedName}"
                        class="ais-range-slider--header ais-facet--header ais-header accordion__title-wrapper"
                      >
                        <h2 class="accordion__title filter__label c-teal uppercase text-utility text-utility--medium">${facet.title}</h2>
                      </label>
                      <div
                        class="ais-facet-${facet.escapedName}-container ais-facet-dropdown-container"
                      ></div>
                    </div>
                  `
              )}
            </div>
          </div>

          <div class="ais-block main-search__results product-grid container ${!__GLOBAL__.showAlgoliaSortBy && !__GLOBAL__.showAlgoliaFilters ? 'no-sidebar' : ''} ">
            <div class="ais-search-header">
              <div class="ais-stats-container"></div>
              <div class="ais-change-display">
                <span
                  class="ais-change-display-block ais-change-display-selected"
                >
                  <i class="fa fa-th-large"></i>
                </span>
                <span class="ais-change-display-list">
                  <i class="fa fa-th-list"></i>
                </span>
              </div>
            </div>

            <hh-product-grid>
              <div class="ais-hits-container ais-results-as-block"></div>
            </hh-product-grid>
          </div>
          <div class="ais-pagination-container"></div>
        </div>
      `
      return productTemplate
    }
  )

  algoliaShopify.hooks.registerHook(
    'afterISStartRemoveDefaultWidget',
    function (defaultWidgets) {
      // Find and return the default pagination widget
      const defaultPaginationWidget = defaultWidgets.find(
        (widget) => widget.$$widgetType === 'ais.pagination'
      )

      return [defaultPaginationWidget]
    }
  )
})

// Custom
document.addEventListener('algolia.autocomplete.afterInit', function () {
  const parent = document.querySelector('.aa-Form')
  const headerActionEl = document.querySelector('.js-header-actions')
  const closeBtn = document.querySelector('.aa-InputWrapperSuffix')
  const submitBtn = document.querySelector('.js-header-search-submit')
  const formSubmit = document.querySelector('.aa-SubmitButton')
  const formSubmitClear = document.querySelector('.aa-ClearButton')


  formSubmit.setAttribute('aria-label','Submit')
  formSubmitClear.setAttribute('aria-label','Clear')
  parent.insertBefore(headerActionEl, closeBtn)
  submitBtn.addEventListener('click', function (e) {
    e.preventDefault()
    formSubmit.click()
  })

  // Fix ADA button has ais-SearchBox-submit
  const applyAriaLabels = () => {
    document.querySelectorAll('.ais-SearchBox-submit').forEach(btn => {
      if (!btn.hasAttribute('aria-label') || btn.getAttribute('aria-label').trim() === '') {
        btn.setAttribute('aria-label', 'Submit the search query');
      }
    });
    document.querySelectorAll('.ais-SearchBox-reset').forEach(btn => {
      if (!btn.hasAttribute('aria-label') || btn.getAttribute('aria-label').trim() === '') {
        btn.setAttribute('aria-label', 'Clear the search query');
      }
    });
  };
  applyAriaLabels();
  const observer = new MutationObserver(applyAriaLabels);
  observer.observe(document.body, { childList: true, subtree: true });

  // Fix ADA Combobox Search
  const root = document.querySelector('.aa-Autocomplete[role="combobox"]');
  if (!root) return;
  const label = root.querySelector('label#autocomplete-0-label[for="autocomplete-0-input"]');
  const submit = root.querySelector('.aa-SubmitButton[type="submit"]');
  const input  = root.querySelector('#autocomplete-0-input');
  const listbox = document.getElementById('js-autocomplete-container');
  if (label && submit && label.contains(submit)) {
    label.parentNode.insertBefore(submit, label);
  }
  if (label) {
    label.removeAttribute('aria-label');
    if (!label.textContent.trim()) label.textContent = 'Search';
    label.classList.add('sr-only');
  }
  if (!root.getAttribute('aria-labelledby') && label?.id) {
    root.setAttribute('aria-labelledby', label.id);
  }
  if (submit && !submit.getAttribute('aria-label')) {
    submit.setAttribute('aria-label', 'Search');
  }
  if (listbox) {
    if (!listbox.hasAttribute('role')) listbox.setAttribute('role','listbox');
    [root, input].forEach(el => el && el.setAttribute('aria-controls', 'js-autocomplete-container'));
    const sync = () => {
      const open = !listbox.hasAttribute('hidden') && listbox.style.display !== 'none';
      root.setAttribute('aria-expanded', String(open));
    //   if (input) input.setAttribute('aria-expanded', String(open));
    };
    new MutationObserver(sync).observe(listbox, {attributes:true, attributeFilter:['hidden','style','class']});
    sync();
  }

})

document.addEventListener('algolia.instantSearch.onRender', function () {
  // Search results render both Non-VIP and VIP price states.
  // Apply current state immediately, then confirm fresh cart state.
  requestAnimationFrame(() => {
    applyAlgoliaVipPriceState()
  })
  scheduleAlgoliaVipPriceSync(undefined, 0)

  // clicking anywhere on the product card navigates to the product page
  // so we need to prevent the default behavior for all elements except <a> tags
  const hitsElements = document.querySelectorAll('li.ais-Hits-item')
  hitsElements.forEach((item) => {
    item.addEventListener('click', (e) => {
      if (e.target.closest('a')) {
        return
      }

      e.preventDefault()
      e.stopPropagation()
    })
  })

  // for some reason the broadcast submit button element "on" and "event" attributes are not being set
  // so we need to set them manually and trigger the form submit when the button is clicked
  const broadcastElements = document.querySelectorAll(
    'li.ais-Hits-item .product-card__atc hh-broadcast:has(button.js-submit-button)'
  )
  broadcastElements.forEach((element) => {
    element.on = 'productAdded'
    element.event = 'modal'

    const submitButton = element.querySelector('button.js-submit-button')
    if (!submitButton) {
      return
    }

    submitButton.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()

      const hhForm = event.target.closest('hh-product-form')
      if (!hhForm) {
        return
      }

      hhForm.onSubmitHandler(event)
    })
  })

  if (nonInventoriedProductArr.length <= 0) return

  let templateSection = ''

  nonInventoriedProductArr.map((hit) => {
    const bzvId = () => {
      if (hit.meta?.bzv?.review_product) {
        const productId = hit.meta?.bzv?.review_product.split('/')
        return productId[productId.length - 1]
      } else {
        return hit.id
      }
    }

    const tags = function () {
      if (
        hit.meta.description?.tags &&
        hit.template_suffix == 'non-inventoried'
      ) {
        let tagsElem = ''
        hit.meta.description?.tags.forEach((tag) => {
          tagsElem += `<span class="product-card__tag">${tag}</span>`
        })
        return tagsElem
      }
    }

    templateSection += `
      <li class="ais-Hits-item">
        <hh-product-card-variants
          class="ais-hit ais-product custom-product"
          data-algolia-index="${hit.index}"
          data-algolia-position="${hit.productPosition}"
          data-algolia-queryid="${hit.queryID}"
          data-algolia-objectid="${hit.objectID}"
          data-handle="${hit.handle}"
          data-variant-id="${hit.objectID}"
          data-distinct="${hit._distinct}"
          data-product-id="${hit.id}"
        >
          <a
            class="js-anchor product-card__link"
            href="${algoliaShopify.helpers.instantsearchLink(hit)}"
          >
            <div class="product-card__media">
              <img
                class="ais-hit--picture product-media"
                src="${algoliaShopify.helpers.largeImage(hit)}"
                alt="${hit.title} - ${hit.variant_title}"
                loading="lazy"
              />
            </div>
          </a>
          <div class="product-card__info-wrapper">
            <div class="product-card__info product-card__entry">
              ${
                hit.template_suffix == 'non-inventoried'
                  ? `<a
                      href="${algoliaShopify.helpers.instantsearchLink(hit)}"
                      ><p
                        class="product-card__description text-utility text-utility--small c-gold"
                      >
                        ${hit.product_type}
                      </p></a
                    >`
                  : ''
              }
              <a
                class="product-card__title--link"
                href="${algoliaShopify.helpers.instantsearchLink(hit)}"
              >
                <p
                  class="product-card__title text-utility text-utility--medium c-teal"
                >
                  ${hit.title}
                </p>
              </a>
              ${
                hit.meta.description?.secondary &&
                hit.template_suffix != 'non-inventoried'
                  ? `<p
                      class="product-card__description text-utility text-utility--small c-gold"
                    >
                      <a href="${algoliaShopify.helpers.instantsearchLink(hit)}"
                        >${hit.meta.description.secondary[0]}</a
                      >
                    </p>`
                  : ''
              }
              ${
                hit.meta.description?.entry &&
                hit.template_suffix == 'non-inventoried'
                  ? `<div
                      class="product-card__description gold text-utility text-utility--small text-utility--alt c-gold"
                    >
                      <a href="${algoliaShopify.helpers.instantsearchLink(hit)}"
                        >${hit.meta.description?.entry[0]}</a
                      >
                    </div>`
                  : ''
              }
              ${
                hit.meta.description?.tags &&
                hit.template_suffix == 'non-inventoried'
                  ? `<p class="product-card__tags text-utility text-utility--smaller text-utility--medium">
                    ${tags()}
                  </p>
              `
                  : ''
              }
              <div
                data-bv-show="inline_rating"
                data-bv-product-id="${bzvId()}"
                data-bv-redirect-url="${algoliaShopify.helpers.instantsearchLink(
                  hit
                )}"
                data-bv-seo="false"
              ></div>
              ${
                hit.template_suffix == 'non-inventoried' &&
                `<div class="product-card__atc">
                  <hh-button-wrapper theme="primary" class="view-bundle">
                    <a href="${algoliaShopify.helpers.instantsearchLink(hit)}">
                      View Products
                    </a>
                  </hh-button-wrapper>
                </div>`
              }
            </div>
          </div>
        </hh-product-card-variants>
      </li>`
  })

  if (typeof $BV !== 'undefined') $BV.ui()

  // const flavorSection =
  //   '<div class="ais-results-as-block"><h2 class="main-search__products-title mv0 mb1 uppercase light center">Blends</h2><div class="ais-Hits"><ol class="ais-Hits-list">' +
  //   templateSection +
  //   '</ol></div></div>'

  // document
  //   .querySelector('.custom-flavor-container')
  //   .insertAdjacentHTML('beforebegin', flavorSection)
})

document.addEventListener('click', function(e) {
  const el = e.target.closest('.js-anchor.product-card__link');
  if (!el) return;
  const productCard = el.closest('hh-product-card-variants')
  if (!productCard) return;

  const queryID = productCard.dataset.algoliaQueryid;
  const variantID = productCard.dataset.variantId;
  const productID = productCard.dataset.productId;
  const indexName = productCard.dataset.algoliaIndex;
  const CLICK_STORAGE_KEY = 'algolia_analytics_clicked_objects';
  const prev = JSON.parse(localStorage.getItem(CLICK_STORAGE_KEY) || '[]');
  const existing = prev.find(
    item => item.index === indexName && item.productId === productID
  );

  if (existing) {
    if (!existing.objectIDs.includes(variantID)) {
      existing.objectIDs.push(variantID);
    }
  } else {
    prev.push({
      index: indexName,
      queryID,
      objectIDs: [variantID],
      productId: productID,
    });
  }

  localStorage.setItem(CLICK_STORAGE_KEY, JSON.stringify(prev));
})